'use client'

import {
  createContext,
  useState,
  useContext,
  useEffect,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
  type FC,
} from 'react'
import type { App } from '@/payload-types'

type HeaderContextType = {
  theme: 'dark' | 'light' | null
  setTheme: Dispatch<SetStateAction<'dark' | 'light' | null>>
  showMenu: boolean
  setShowMenu: Dispatch<SetStateAction<boolean>>
  focus: string | null
  setFocus: (focus: string | null) => void
  stage: number
  setStage: (stage: number) => void
  selectedCategory: number | null
  setSelectedCategory: (selectedCategory: number | null) => void
  selectedApps: App[]
  setSelectedApps: (selectedApps: App[] | ((prev: App[]) => App[])) => void
  changeSelected: (index: number) => void
  resetStage: () => void
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined)

export const useHeaderContext = () => {
  const context = useContext(HeaderContext)
  if (!context) {
    throw new Error('useHeaderContext must be used within a HeaderContextProvider')
  }
  return context
}

type HeaderContextProviderProps = {
  children: ReactNode
}

export const HeaderContextProvider: FC<HeaderContextProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<'dark' | 'light' | null>(null)
  const [showMenu, setShowMenu] = useState(false)
  const [focus, setFocus] = useState<string | null>(null)

  const [selectedCategory, setSelectedCategory] = useState<number | null>(null)
  const [selectedApps, setSelectedApps] = useState<App[]>([])
  const [stage, setStage] = useState(0)

  const changeSelected = (id: number) => {
    if (selectedCategory === id) return null
    setSelectedApps([])
    setSelectedCategory(id)
    setStage(2)
  }

  const resetStage = () => {
    setStage(0)
    setSelectedCategory(null)
    setSelectedApps([])
  }

  useEffect(() => {
    resetStage()
  }, [showMenu])

  return (
    <HeaderContext.Provider
      value={{
        theme,
        setTheme,
        showMenu,
        setShowMenu,
        focus,
        setFocus,
        stage,
        setStage,
        selectedCategory,
        setSelectedCategory,
        selectedApps,
        setSelectedApps,
        changeSelected,
        resetStage,
      }}
    >
      {children}
    </HeaderContext.Provider>
  )
}
